/**
 * `/t/:number` (and `/tickets/by-number/:number`) — the landing for links in
 * Ivanti notification emails.
 *
 * Email knows a ticket by its human number; the portal addresses it by id. This
 * page resolves one to the other and replaces itself with `/tickets/:id`, so
 * Back from the ticket goes to wherever the reader came from, not through here.
 * A number that is not theirs (403) or not anyone's (404) reads the same way —
 * "we couldn't find it" — because telling a stranger that a ticket exists is
 * already more than they should learn.
 */

import { ArrowLeft, Icon, SearchX } from "@norican/icons";
import { Button } from "@norican/react";
import { Navigate, useNavigate, useParams } from "react-router";

import {
  EmptyStateBlock,
  ErrorState,
  LoadingState,
  normaliseApiError,
  PageContainer,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import { logger } from "@/lib/logger";

import { useTicketByNumber, useTranslateTo } from "./api/queries";
import { TicketErrorBoundary } from "./components/ticket-error-boundary";
import { isTicketNotFound, normaliseTicketNumber } from "./lib/ticket-number";

export function TicketNumberRedirectPage() {
  return (
    <TicketErrorBoundary>
      <TicketNumberRedirect />
    </TicketErrorBoundary>
  );
}

function TicketNumberRedirect() {
  const { t } = useT("tickets");
  const navigate = useNavigate();
  const { number } = useParams<{ number: string }>();
  const ticketNumber = normaliseTicketNumber(number);
  const translateTo = useTranslateTo();
  const lookup = useTicketByNumber(ticketNumber, translateTo);
  const normalised = lookup.isError ? normaliseApiError(lookup.error) : null;
  const notFound =
    ticketNumber.length === 0 ||
    (normalised !== null && isTicketNotFound(normalised.status, normalised.code));
  useDocumentTitle(
    notFound
      ? t("byNumber.notFoundTitle", { number: ticketNumber })
      : t("byNumber.loadingTitle", { number: ticketNumber }),
  );

  const backToList = (
    <Button
      variant="outline"
      tone="neutral"
      startIcon={<Icon icon={ArrowLeft} size="sm" />}
      onClick={() => void navigate("/tickets")}
    >
      {t("detail.backToList")}
    </Button>
  );

  if (lookup.data !== undefined) {
    return <Navigate to={`/tickets/${encodeURIComponent(lookup.data.ticket_id)}`} replace />;
  }

  if (notFound) {
    logger.debug("[tickets] deep link number not found:", ticketNumber, normalised?.code);
    return (
      // One heading, the specific one. A generic "We couldn't open this
      // ticket" page title above "We couldn't find ticket 123" said the same
      // thing twice.
      <PageContainer title={t("byNumber.notFoundTitle", { number: ticketNumber })}>
        <EmptyStateBlock
          icon={<Icon icon={SearchX} size="lg" className="text-fg-muted" />}
          title={t("byNumber.notFoundBody")}
          description={null}
          actions={backToList}
        />
      </PageContainer>
    );
  }

  if (lookup.isError) {
    // Not "no such ticket": the provider could not be asked. Worth a retry.
    return (
      <PageContainer title={t("detail.errorTitle")}>
        <ErrorState
          error={lookup.error}
          onRetry={() => void lookup.refetch()}
          actions={backToList}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer title={t("byNumber.loadingTitle", { number: ticketNumber })}>
      <LoadingState />
    </PageContainer>
  );
}
